import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Plus } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Button, Skeleton } from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useServiceAdvancedStrategies,
  useServiceAdvancedEnabledSites,
  useDeleteServiceAdvancedStrategy,
  type AdvancedStrategyListItem,
} from '@/api/waap';
import { errMessage } from '@/pages/services/waap/waap-util';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './AdvancedStrategyPanel.module.css';

const muted = (v: React.ReactNode) => <span className={styles.muted}>{v}</span>;

/**
 * Shared rule list for the strategy-based Advanced-Protection tabs (Anti-Leech,
 * Brute Force, …) — all backed by `waf_advanced_strategy` under a `type`. Renders
 * the ID / Name / Description / Website / Operation table plus add/edit/delete.
 * The owning tab supplies the type + copy + route base.
 */
export function AdvancedStrategyPanel({
  serviceId,
  base,
  type,
  title,
  description,
  addLabel,
  /** Route segment under `${base}/aiwaf/advanced/` for the add/edit form. */
  formSegment,
  noun,
}: {
  serviceId: string;
  base: string;
  type: number;
  title: string;
  description: string;
  addLabel: string;
  formSegment: string;
  noun: string;
}) {
  const navigate = useNavigate();
  const { show } = useToast();
  const [pendingDelete, setPendingDelete] = useState<AdvancedStrategyListItem | null>(null);

  const { data: strategies = [], isLoading } = useServiceAdvancedStrategies(serviceId, type);
  const { data: sites = [] } = useServiceAdvancedEnabledSites(serviceId);

  const siteName = useMemo(() => {
    const m = new Map<number, string>();
    for (const s of sites) m.set(s.id, s.domain);
    return (id: number) => m.get(id) ?? `#${id}`;
  }, [sites]);

  const del = useDeleteServiceAdvancedStrategy(serviceId, type, {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'advanced_protection_rule', 'delete');
      show({ tone: 'success', title: `${noun} deleted` });
      setPendingDelete(null);
    },
    onError: (e) => show({ tone: 'danger', title: 'Delete failed', description: errMessage(e) }),
  });

  const formPath = (suffix: string) => `${base}/aiwaf/advanced/${formSegment}/${suffix}`;

  const columns = useMemo<ColumnDef<AdvancedStrategyListItem>[]>(
    () => [
      { accessorKey: 'id', header: 'ID', cell: ({ row }) => muted(row.original.id) },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => <span className={styles.name}>{row.original.name}</span>,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ row }) => row.original.description || muted('—'),
      },
      {
        id: 'website',
        header: 'Website',
        cell: ({ row }) => {
          const ids = row.original.sites ?? [];
          return ids.length ? ids.map(siteName).join(', ') : muted('—');
        },
      },
      {
        id: 'actions',
        header: 'Operation',
        cell: ({ row }) => (
          <div className={styles.rowActions}>
            <Button variant="ghost" size="sm" onClick={() => navigate(formPath(String(row.original.id)))}>
              Edit
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setPendingDelete(row.original)}>
              Delete
            </Button>
          </div>
        ),
      },
    ],
    // formSegment/base must be deps: this panel instance is reused across tabs
    // (same JSX position), so a stale closure would navigate to the wrong form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [siteName, base, formSegment, navigate],
  );

  return (
    <Section
      title={title}
      description={description}
      actions={
        <Button startIcon={<Plus size={16} />} onClick={() => navigate(formPath('new'))}>
          {addLabel}
        </Button>
      }
    >
      {isLoading ? (
        <div className={styles.skeletonList}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className={styles.skelRow} />
          ))}
        </div>
      ) : (
        <div className={styles.tableScroll}>
          <DataTable
            columns={columns}
            data={strategies}
            emptyState={
              <EmptyState
                size="sm"
                icon={<ShieldCheck size={20} />}
                title={`No ${noun.toLowerCase()}s`}
                description={`Add a rule to start protecting your websites.`}
              />
            }
          />
        </div>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title={`Delete ${noun.toLowerCase()}?`}
        description={`Delete "${pendingDelete?.name}"? This removes its protection from the websites it applies to. This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={del.isPending}
        onConfirm={() => pendingDelete && del.mutate(pendingDelete.id)}
      />
    </Section>
  );
}
